import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
    FaMapMarkerAlt,
    FaEnvelope,
    FaPhoneAlt,
    FaClock,
    FaWhatsapp,
    FaFacebookF,
    FaInstagram,
    FaLinkedinIn,
    FaPaperPlane,
    FaCheckCircle,
    FaBriefcase,
    FaGraduationCap,
    FaPlaneDeparture,
    FaBuilding,
    FaPassport,
} from "react-icons/fa";

const VISA_TYPES = [
    "Work Visa",
    "Student Visa",
    "Tourist / Visit Visa",
    "Business Visa",
    "PR / Permanent Residency",
    "Document Attestation",
    "Trade License",
    "Other",
];

const CONTACT_INFO = [
    {
        Icon: FaMapMarkerAlt,
        label: "Office Address",
        value: "Lake Central Tower, Business Bay, Dubai, UAE",
        href: "https://maps.google.com/?q=Lake+Central+Tower+Business+Bay+Dubai",
        color: "bg-blue-50 text-blue-600",
    },
    {
        Icon: FaPhoneAlt,
        label: "Phone Number",
        value: "+971 54 766 2665",
        href: "tel:+971547662665",
        color: "bg-green-50 text-green-600",
    },
    {
        Icon: FaEnvelope,
        label: "Email Address",
        value: "info@lumisdocumentsclearing.com",
        href: "mailto:info@lumisdocumentsclearing.com",
        color: "bg-purple-50 text-purple-600",
    },
    {
        Icon: FaClock,
        label: "Working Hours",
        value: "Mon – Fri: 9:30 AM – 6:30 PM",
        href: null,
        color: "bg-orange-50 text-orange-500",
    },
];

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        visaType: "",
        country: "",
        message: "",
    });
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState("");

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Name is required.";
        if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
            e.email = "A valid email is required.";
        if (!form.message.trim()) e.message = "Please enter your message.";
        return e;
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: "" });
        setServerError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setLoading(true);
        setServerError("");
        try {
            const res = await fetch(`${API_URL}/api/enquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setSubmitted(true);
            } else {
                setServerError(data.message || "Something went wrong. Please try again.");
            }
        } catch (err) {
            setServerError("Unable to reach the server. Please check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Helmet>
                <title>Contact Us | Lumis Documents Clearing Dubai</title>
                <meta name="description" content="Get in touch with Lumis Documents Clearing today. Book a free consultation for expert immigration advice at our Business Bay office." />
            </Helmet>
            {/* HERO */}
            <section
                className="h-[420px] flex items-center justify-center text-white bg-cover bg-center relative"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop')",
                }}
            >
                <div className="absolute inset-0 bg-black/60" />

                <div className="relative text-center px-6">
                    <p className="tracking-widest text-blue-400 text-sm mb-3 uppercase">
                        We're Here to Help
                    </p>

                    <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>

                    <p className="text-gray-200 max-w-xl mx-auto mb-5">
                        Speak with our expert immigration consultants today. We offer free
                        consultations for all visa types and destinations.
                    </p>

                    <div className="text-gray-300 text-sm">
                        <Link to="/" className="hover:text-white transition">
                            Home
                        </Link>
                        <span className="mx-2">/</span>
                        <span className="text-white font-medium">Contact</span>
                    </div>
                </div>
            </section>

            {/* QUICK CONTACT BAR */}
            <section className="bg-blue-600 text-white py-5">
                <div className="max-w-6xl mx-auto px-6 flex flex-wrap justify-center md:justify-between items-center gap-6 text-sm">
                    <a
                        href="tel:+971547662665"
                        className="flex items-center gap-3 hover:text-blue-200 transition"
                    >
                        <FaPhoneAlt />
                        <span>+971 54 766 2665</span>
                    </a>

                    <a
                        href="mailto:info@lumisdocumentsclearing.com"
                        className="flex items-center gap-3 hover:text-blue-200 transition"
                    >
                        <FaEnvelope />
                        <span>info@lumisdocumentsclearing.com</span>
                    </a>

                    <a
                        href="https://wa.me/971547662665"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-green-500 hover:bg-green-600 px-5 py-2 rounded-full font-semibold transition"
                    >
                        <FaWhatsapp size={18} />
                        <span>WhatsApp Us</span>
                    </a>
                </div>
            </section>

            {/* CONTACT INFO CARDS */}
            <section className="py-20 bg-gray-100">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-center mb-3">Get in Touch</h2>
                    <p className="text-center text-gray-500 mb-14">
                        Multiple ways to reach us — choose what's convenient for you
                    </p>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {CONTACT_INFO.map(({ Icon, label, value, href, color }) => (
                            <div
                                key={label}
                                className="bg-white rounded-xl p-7 shadow hover:shadow-xl hover:-translate-y-2 transition duration-300 text-center"
                            >
                                <div
                                    className={`w-14 h-14 ${color} rounded-full flex items-center justify-center mx-auto mb-4`}
                                >
                                    <Icon size={22} />
                                </div>
                                <h3 className="font-semibold text-gray-800 mb-2">{label}</h3>
                                {href ? (
                                    <a
                                        href={href}
                                        target={href.startsWith("http") ? "_blank" : undefined}
                                        rel="noopener noreferrer"
                                        className="text-sm text-gray-500 hover:text-blue-600 transition leading-relaxed"
                                    >
                                        {value}
                                    </a>
                                ) : (
                                    <p className="text-sm text-gray-500 leading-relaxed">{value}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FORM + MAP */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-start">

                        {/* CONTACT FORM */}
                        <div>
                            <h2 className="text-3xl font-bold mb-3">
                                Send Us a Message
                            </h2>
                            <p className="text-gray-500 mb-8">
                                Fill in the form below and one of our consultants will get back
                                to you within 24 hours.
                            </p>

                            {submitted ? (
                                /* SUCCESS STATE */
                                <div className="flex flex-col items-center text-center py-16 bg-green-50 rounded-2xl border border-green-100">
                                    <FaCheckCircle className="text-green-500 text-6xl mb-5" />
                                    <h3 className="text-2xl font-bold text-gray-800 mb-3">
                                        Message Sent!
                                    </h3>
                                    <p className="text-gray-500 max-w-sm mb-6">
                                        Thank you, <strong>{form.name}</strong>! We've received your
                                        enquiry and will contact you within 24 hours.
                                    </p>
                                    <button
                                        onClick={() => {
                                            setForm({
                                                name: "",
                                                email: "",
                                                phone: "",
                                                visaType: "",
                                                country: "",
                                                message: "",
                                            });
                                            setSubmitted(false);
                                        }}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition"
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                                    {/* NAME + EMAIL */}
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={form.name}
                                                onChange={handleChange}
                                                placeholder="e.g. Ahmed Al-Rashid"
                                                className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${errors.name
                                                    ? "border-red-400 bg-red-50"
                                                    : "border-gray-200 bg-gray-50"
                                                    }`}
                                            />
                                            {errors.name && (
                                                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Email Address <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={form.email}
                                                onChange={handleChange}
                                                placeholder="e.g. ahmed@email.com"
                                                className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${errors.email
                                                    ? "border-red-400 bg-red-50"
                                                    : "border-gray-200 bg-gray-50"
                                                    }`}
                                            />
                                            {errors.email && (
                                                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* PHONE + VISA TYPE */}
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={form.phone}
                                                onChange={handleChange}
                                                placeholder="e.g. +971 50 000 0000"
                                                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Visa / Service Type
                                            </label>
                                            <select
                                                name="visaType"
                                                value={form.visaType}
                                                onChange={handleChange}
                                                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                            >
                                                <option value="">Select a service...</option>
                                                {VISA_TYPES.map((v) => (
                                                    <option key={v} value={v}>
                                                        {v}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* DESTINATION */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Target Country / Destination
                                        </label>
                                        <input
                                            type="text"
                                            name="country"
                                            value={form.country}
                                            onChange={handleChange}
                                            placeholder="e.g. Canada, UK, Australia..."
                                            className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                        />
                                    </div>

                                    {/* MESSAGE */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Your Message <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            name="message"
                                            value={form.message}
                                            onChange={handleChange}
                                            rows={5}
                                            placeholder="Tell us about your immigration goals, current situation or any questions you have..."
                                            className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition resize-none ${errors.message
                                                ? "border-red-400 bg-red-50"
                                                : "border-gray-200 bg-gray-50"
                                                }`}
                                        />
                                        {errors.message && (
                                            <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                                        )}
                                    </div>

                                    {/* SERVER ERROR */}
                                    {serverError && (
                                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                                            ⚠️ {serverError}
                                        </div>
                                    )}

                                    {/* SUBMIT */}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-xl transition flex items-center justify-center gap-3 shadow-md hover:shadow-lg"
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                </svg>
                                                Sending...
                                            </>
                                        ) : (
                                            <><FaPaperPlane /> Send Message</>
                                        )}
                                    </button>

                                    <p className="text-xs text-gray-400 text-center">
                                        🔒 Your information is kept private and secure. We'll never
                                        share your data.
                                    </p>
                                </form>
                            )}
                        </div>

                        {/* RIGHT SIDE — MAP + EXTRA INFO */}
                        <div className="flex flex-col gap-8">
                            {/* MAP */}
                            <div>
                                <h3 className="text-xl font-bold mb-4">Find Our Office</h3>
                                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                                    <iframe
                                        title="Lumis Office Location"
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.5!2d55.264!3d25.186!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f69d1be1a5a3d%3A0x5e54e6b4d12a9f5!2sLake+Central+Tower+Business+Bay!5e0!3m2!1sen!2sae!4v1680000000000!5m2!1sen!2sae"
                                        width="100%"
                                        height="280"
                                        style={{ border: 0 }}
                                        allowFullScreen=""
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>
                                <a
                                    href="https://maps.google.com/?q=Lake+Central+Tower+Business+Bay+Dubai"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 mt-3 text-sm text-blue-600 hover:underline font-medium"
                                >
                                    <FaMapMarkerAlt />
                                    Get Directions on Google Maps
                                </a>
                            </div>

                            {/* QUICK CONNECT */}
                            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
                                <h3 className="text-xl font-bold mb-5">Quick Connect</h3>

                                <div className="flex flex-col gap-4">
                                    <a
                                        href="https://wa.me/971547662665"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-4 bg-green-500 hover:bg-green-600 text-white px-5 py-4 rounded-xl transition font-semibold group"
                                    >
                                        <div className="bg-white/20 p-2 rounded-lg">
                                            <FaWhatsapp size={22} />
                                        </div>
                                        <div>
                                            <p className="font-bold">Chat on WhatsApp</p>
                                            <p className="text-green-100 text-xs">Typically replies within minutes</p>
                                        </div>
                                    </a>

                                    <a
                                        href="tel:+971547662665"
                                        className="flex items-center gap-4 bg-blue-600 hover:bg-blue-700 text-white px-5 py-4 rounded-xl transition font-semibold"
                                    >
                                        <div className="bg-white/20 p-2 rounded-lg">
                                            <FaPhoneAlt size={20} />
                                        </div>
                                        <div>
                                            <p className="font-bold">Call Us Now</p>
                                            <p className="text-blue-200 text-xs">+971 54 766 2665</p>
                                        </div>
                                    </a>

                                    <a
                                        href="mailto:info@lumisdocumentsclearing.com"
                                        className="flex items-center gap-4 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 px-5 py-4 rounded-xl transition font-semibold shadow-sm"
                                    >
                                        <div className="bg-purple-100 text-purple-600 p-2 rounded-lg">
                                            <FaEnvelope size={20} />
                                        </div>
                                        <div>
                                            <p className="font-bold">Send an Email</p>
                                            <p className="text-gray-400 text-xs truncate">info@lumisdocumentsclearing.com</p>
                                        </div>
                                    </a>
                                </div>
                            </div>

                            {/* SOCIAL MEDIA */}
                            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
                                <h3 className="text-xl font-bold mb-5">Follow Us</h3>
                                <div className="flex gap-4">
                                    {[
                                        { Icon: FaFacebookF, label: "Facebook", color: "bg-blue-600 hover:bg-blue-700" },
                                        { Icon: FaInstagram, label: "Instagram", color: "bg-pink-500 hover:bg-pink-600" },
                                        { Icon: FaLinkedinIn, label: "LinkedIn", color: "bg-sky-600 hover:bg-sky-700" },
                                        { Icon: FaWhatsapp, label: "WhatsApp", color: "bg-green-500 hover:bg-green-600" },
                                    ].map(({ Icon, label, color }) => (
                                        <a
                                            key={label}
                                            href="#!"
                                            aria-label={label}
                                            className={`${color} text-white w-11 h-11 rounded-full flex items-center justify-center shadow transition hover:scale-110`}
                                        >
                                            <Icon size={18} />
                                        </a>
                                    ))}
                                </div>
                                <p className="text-sm text-gray-400 mt-4">
                                    Stay updated with visa news and immigration tips.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES WE ASSIST WITH */}
            <section className="py-20 bg-gray-100">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-center mb-3">
                        Enquire About Any Service
                    </h2>
                    <p className="text-center text-gray-500 mb-14">
                        Our consultants are ready to assist with all immigration needs
                    </p>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { Icon: FaBriefcase, title: "Work Visa", desc: "Employment and skilled worker visa guidance for global destinations." },
                            { Icon: FaGraduationCap, title: "Student Visa", desc: "Support for international students applying for study visas worldwide." },
                            { Icon: FaPlaneDeparture, title: "Tourist Visa", desc: "Fast processing for tourist and short-stay visit visas." },
                            { Icon: FaBuilding, title: "Business Visa", desc: "Investor and business visas for entrepreneurs and corporations." },
                            { Icon: FaPassport, title: "PR / Residency", desc: "Permanent residency and long-term immigration pathway assistance." },
                            { Icon: FaEnvelope, title: "Document Services", desc: "Certificate attestation, trade license and document clearing services." },
                        ].map(({ Icon, title, desc }) => (
                            <div
                                key={title}
                                className="group bg-white rounded-xl p-7 shadow hover:shadow-xl hover:-translate-y-2 transition duration-300 flex gap-5 items-start"
                            >
                                <div className="w-12 h-12 flex-shrink-0 bg-blue-50 group-hover:bg-blue-600 rounded-xl flex items-center justify-center transition duration-300">
                                    <Icon className="text-blue-600 group-hover:text-white text-xl transition duration-300" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-800 mb-1 group-hover:text-blue-600 transition">
                                        {title}
                                    </h3>
                                    <p className="text-sm text-gray-500">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA BANNER */}
            <section className="bg-blue-600 text-white py-20 text-center">
                <h2 className="text-3xl font-bold mb-4">
                    Not Sure Where to Start?
                </h2>
                <p className="mb-8 text-blue-100 max-w-xl mx-auto">
                    Book a free consultation with our immigration experts. We'll assess
                    your profile and guide you to the best visa pathway.
                </p>

                <div className="flex justify-center gap-4 flex-wrap">
                    <a
                        href="https://wa.me/971547662665"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-full font-semibold shadow-md transition"
                    >
                        <FaWhatsapp size={18} />
                        Chat on WhatsApp
                    </a>

                    <a
                        href="tel:+971547662665"
                        className="flex items-center gap-2 border border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition"
                    >
                        <FaPhoneAlt size={16} />
                        Call Now
                    </a>
                </div>
            </section>

            {/* FLOATING CONTACT WIDGET */}
            <div className="fixed bottom-6 right-6 flex flex-col items-end gap-4 z-50">
                <div className="bg-white shadow-xl rounded-lg p-4 max-w-xs animate-bounce">
                    <p className="text-sm text-gray-700">💬 Need help with your visa?</p>
                    <p className="text-sm font-semibold text-gray-900">
                        Chat with us on WhatsApp
                    </p>
                </div>

                <Link to="/contact" className="group flex items-center">
                    <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                        Free Consultation
                    </span>
                    <div className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
                        <FaPhoneAlt size={20} />
                    </div>
                </Link>

                <a
                    href="https://wa.me/971547662665"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center"
                >
                    <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                        Chat on WhatsApp
                    </span>
                    <div className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
                        <FaWhatsapp size={22} />
                    </div>
                </a>
            </div>
        </div>
    );
}